import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CardStatement } from 'src/app/models/card-account-statement.model';
import { CardActiveInstalmentAgreements } from 'src/app/models/card-active-instalment-agreements.model';
import { InstalmentAgreementDetails } from 'src/app/models/card-instalment-agreement-details.model';
import { CardInstalmentAgreements } from 'src/app/models/card-instalment-agreements.model';
import { CardStatementsFilter } from 'src/app/models/card-statements-filter.model';
import { CardTransaction } from 'src/app/models/card-transaction.model';
import { Card } from 'src/app/models/card.model';
import { TransactionHistoryList } from 'src/app/models/transaction-history.model';
import {
  createServerDate,
  generatePastDate
} from 'src/app/shared/utils/date.utils';
import * as fromCards from '../../reducers/cards/cards.reducer';
import * as fromDashboard from '../../reducers/dashboard/dashboard.reducer';
import { generateFutureDate } from './../../../shared/utils/date.utils';

export const selectDashboardStateFeature =
  createFeatureSelector<fromDashboard.State>(fromDashboard.dashboardFeatureKey);

export const selectCardStateFeature = createFeatureSelector<fromCards.State>(
  fromCards.cardsFeatureKey
);

export interface CardsViewModel {
  cards: Card[] | null;
  selectedCard: Card | null;
}

export interface TransactionHistoryPath {
  cardToken: string | undefined;
  memberNumber: string | undefined;
  from: string | undefined;
  to: string | undefined;
  cardType: string | undefined;
}
export interface InstalmentAgreementsPath {
  cardToken: string | undefined;
  accountNumber: string | undefined;
}

export interface SelectedCardModel {
  selectedCard: Card | null;
}

export interface CardTransactionHistoryModel {
  selectedCard: Card | null;
  transactionHistory: CardTransaction | null;
}

export interface CardInstalmentAgreementsModel {
  selectedCard: Card | null;
  instalmentAgreements: CardInstalmentAgreements | null;
}
export interface CardActiveInstalmentAgreementsModel {
  selectedCard: Card | null;
  activeInstalmentAgreements: CardActiveInstalmentAgreements | null;
}

export interface CardIdModel {
  cardId: string | undefined;
}

export interface CardTransactionsDetailsModel {
  cardTransactionsDetails: TransactionHistoryList | null;
}

export interface CardInstalAgreementsDetailsModel {
  cardInstalAgreementsDetails: InstalmentAgreementDetails | null;
}

export interface CardAccStatementsModel {
  selectedCard: Card | null;
  statements: CardStatement[] | null;
}

export interface CardStatementsFiltersModel {
  statementsFilters: CardStatementsFilter | undefined;
  selectedCard: Card | null;
}

export const selectAllCardsViewModel = createSelector(
  selectDashboardStateFeature,
  selectCardStateFeature,
  (
    dashboardState: fromDashboard.State,
    cardsState: fromCards.State
  ): CardsViewModel => {
    return {
      cards: dashboardState.dashboard ? dashboardState.dashboard.cards : null,
      selectedCard: cardsState.selectedCard
    };
  }
);

export const selectCardTransactionHistoryModel = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardTransactionHistoryModel => {
    return {
      selectedCard: state.selectedCard,
      transactionHistory: state.transactionHistory
    };
  }
);

export const selectCardTransactionHistoryPath = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): TransactionHistoryPath => {
    return {
      cardToken: state.selectedCard?.cardToken,
      memberNumber: state.selectedCard?.memberNumber,
      cardType: state.selectedCard?.cardType,
      from: createServerDate(generatePastDate(-1, 3, 0).toString()),
      to: createServerDate(generateFutureDate(0, 0, 0).toString())
    };
  }
);

export const selectCardInstalmentAgreementsModel = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardInstalmentAgreementsModel => {
    return {
      selectedCard: state.selectedCard,
      instalmentAgreements: state.instalmentAgreements
    };
  }
);

export const selectCardActiveInstalmentAgreementsModel = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardActiveInstalmentAgreementsModel => {
    return {
      selectedCard: state.selectedCard,
      activeInstalmentAgreements: state.activeInstalmentAgreements
    };
  }
);

export const selectCardInstalmentAgreementsPath = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): InstalmentAgreementsPath => {
    return {
      cardToken: state.selectedCard?.cardToken,
      accountNumber: state.selectedCard?.accountNumber
    };
  }
);

export const selectCardViewModel = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): SelectedCardModel => {
    return {
      selectedCard: state.selectedCard
    };
  }
);

export const selectCardId = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardIdModel => {
    return {
      cardId: state.selectedCard?.cardId
    };
  }
);

export const selectCardTransactionsDetails = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardTransactionsDetailsModel => {
    return {
      cardTransactionsDetails: state.transactionHistoryList
    };
  }
);

export const selectCardInstalmentDetails = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardInstalAgreementsDetailsModel => {
    return {
      cardInstalAgreementsDetails: state.cardInstalAgreementsDetails
    };
  }
);
export const selectCardAccStatements = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardAccStatementsModel => {
    return {
      selectedCard: state.selectedCard,
      statements: state.statements
    };
  }
);

export const selectActiveCard = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardStatementsFiltersModel => {
    return {
      selectedCard: state.selectedCard,
      statementsFilters: state.cardStatementsFilters
    };
  }
);

export const selectCardStatementsFilters = createSelector(
  selectCardStateFeature,
  (state: fromCards.State): CardStatementsFiltersModel => {
    return {
      statementsFilters: state.cardStatementsFilters,
      selectedCard: state.selectedCard
    };
  }
);
